"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { Autocomplete } from "@base-ui/react/autocomplete";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { usePortalContainer } from "../../contexts/index.js";
import { useVisualViewportHeightVar } from "../../hooks/useVisualViewportHeightVar.js";
import { cn } from "../../utils/cn.js";
import { DRAWER_SWIPE_IGNORE_ATTRIBUTE, TRANSFORMER_MAX_MOBILE_WIDTH_PX } from "../../utils/constants.js";
import { beginIosKeyboardRelay } from "../../utils/iosKeyboardFocus.js";
import { mergeRefs } from "../../utils/mergeRefs.js";
import { Drawer } from "../Drawer/index.js";
import { IconButton } from "../IconButton/index.js";
import { InputButton } from "../InputButton/index.js";
import { InputText } from "../InputText/index.js";
import { OverlaySearchHeader } from "../OverlaySearchHeader/index.js";
import { handleAutocompleteFocusOutClose } from "./autocompleteInteractionHandlers.js";
import { baseAutocompleteVariants } from "./BaseAutocomplete.variants.js";
import { ItemListContent } from "./ItemListContent.js";
function BaseAutocomplete(props) {
    const { items, onInputValueChange, onSelectItem, onMenuOpen, defaultSelectedItem, name, iconName = 'search', label = 'Where to?', className, errorMessage, inputRef, updateInputValueOnSelect = true, required = false, filterChipData = [], handleFilterChipClick, selectedFilterChipData = new Set(), placeholder, renderEmpty, inputButtonRef, collisionAvoidance, iosKeyboardHackTimeout: _iosKeyboardHackTimeout = 300, autoHighlight = 'always', rightIconButtonProps, getItemFirstLineSuffix } = props;
    const showDrawerOverlay = useMediaQuery({
        maxWidth: TRANSFORMER_MAX_MOBILE_WIDTH_PX
    });
    const portalContainer = usePortalContainer();
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState(defaultSelectedItem?.itemName ?? '');
    const hasFiredMenuOpenRef = useRef(false);
    const drawerInputRef = useRef(null);
    const iosTransferFocusRef = useRef(null);
    const localInputRef = useRef(null);
    const { autocompleteItems, isGrouped } = useMemo(()=>{
        const hasGroupHeaders = items.some((item)=>item.isGroupHeader);
        if (hasGroupHeaders) {
            const groups = [];
            let currentGroup = null;
            for (const item of items)if (item.isGroupHeader) {
                if (currentGroup && currentGroup.items.length > 0) groups.push(currentGroup);
                currentGroup = {
                    label: item.itemName,
                    items: []
                };
            } else if (currentGroup) currentGroup.items.push(item);
            if (currentGroup && currentGroup.items.length > 0) groups.push(currentGroup);
            return {
                autocompleteItems: groups,
                isGrouped: groups.length > 0
            };
        }
        return {
            autocompleteItems: items,
            isGrouped: false
        };
    }, [
        items
    ]);
    const setOpen = useCallback((open)=>{
        setIsOpen(open);
    }, []);
    const focusAutocompleteInput = useCallback(()=>{
        const input = showDrawerOverlay ? drawerInputRef.current : localInputRef.current;
        input?.focus();
    }, [
        showDrawerOverlay
    ]);
    const onOpenChange = useCallback((open, eventDetails)=>{
        if (handleAutocompleteFocusOutClose(open, eventDetails)) return;
        setOpen(open);
        if (open && !hasFiredMenuOpenRef.current) {
            hasFiredMenuOpenRef.current = true;
            onMenuOpen?.();
        }
        if (!open) hasFiredMenuOpenRef.current = false;
    }, [
        onMenuOpen,
        setOpen
    ]);
    const handleCloseDrawer = useCallback(()=>{
        setOpen(false);
    }, [
        setOpen
    ]);
    const openDropdownAndNotify = useCallback(()=>{
        setOpen(true);
        if (!hasFiredMenuOpenRef.current) {
            hasFiredMenuOpenRef.current = true;
            onMenuOpen?.();
        }
    }, [
        setOpen,
        onMenuOpen
    ]);
    const onInputClick = useCallback((e)=>{
        e.target.select?.();
    }, []);
    const onInputButtonClick = useCallback((_e)=>{
        if (showDrawerOverlay) iosTransferFocusRef.current = beginIosKeyboardRelay();
        openDropdownAndNotify();
    }, [
        openDropdownAndNotify,
        showDrawerOverlay
    ]);
    const handleInputValueChange = useCallback((value)=>{
        setInputValue(value);
        onInputValueChange(value);
    }, [
        onInputValueChange
    ]);
    const handleItemSelect = useCallback((item, index)=>{
        if (updateInputValueOnSelect) setInputValue(item.itemName);
        const flatItems = isGrouped ? autocompleteItems.flatMap((group)=>group.items) : autocompleteItems;
        const resolvedIndex = index ?? flatItems.indexOf(item);
        onSelectItem?.(item, resolvedIndex >= 0 ? resolvedIndex : void 0);
        setOpen(false);
    }, [
        updateInputValueOnSelect,
        onSelectItem,
        setOpen,
        autocompleteItems,
        isGrouped
    ]);
    useEffect(()=>{
        if (!isOpen) {
            hasFiredMenuOpenRef.current = false;
            iosTransferFocusRef.current?.cancel();
            iosTransferFocusRef.current = null;
        }
    }, [
        isOpen
    ]);
    useEffect(()=>()=>{
            iosTransferFocusRef.current?.cancel();
            iosTransferFocusRef.current = null;
        }, []);
    const handleDrawerOpenComplete = useCallback(()=>{
        const transferFocus = iosTransferFocusRef.current;
        if (transferFocus) {
            transferFocus(drawerInputRef.current);
            iosTransferFocusRef.current = null;
        } else drawerInputRef.current?.focus({
            preventScroll: true
        });
        const input = drawerInputRef.current;
        if (input) {
            const len = input.value.length;
            input.setSelectionRange(len, len);
        }
    }, []);
    const setOverlayPopupRef = useVisualViewportHeightVar(showDrawerOverlay && isOpen);
    const slots = useMemo(()=>baseAutocompleteVariants({
            isMobile: showDrawerOverlay
        }), [
        showDrawerOverlay
    ]);
    const { rootSlot, popupSlot, positionerSlot, listSlot, emptySlot, groupLabelSlot, overlayInputSlot, iconSlot } = slots;
    const shouldShowError = errorMessage && !isOpen;
    const inputTextTrigger = useCallback(({ ref: baseRef, ...props })=>/*#__PURE__*/ jsx(InputText, {
            ...props,
            size: "md",
            ref: mergeRefs(baseRef, inputRef, drawerInputRef, localInputRef),
            iconLeft: iconName ?? void 0,
            isInset: true,
            label: label,
            "data-testid": "autocomplete-input-in-overlay",
            name: name,
            errorMessage: shouldShowError && !showDrawerOverlay ? errorMessage : void 0,
            hasExternalError: Boolean(shouldShowError && !showDrawerOverlay),
            required: required,
            placeholder: placeholder,
            onClick: onInputClick,
            autoFocus: false,
            onFocus: openDropdownAndNotify,
            rightIconButtonProps: rightIconButtonProps
        }), [
        errorMessage,
        iconName,
        inputRef,
        onInputClick,
        label,
        name,
        openDropdownAndNotify,
        drawerInputRef,
        localInputRef,
        placeholder,
        required,
        shouldShowError,
        showDrawerOverlay,
        rightIconButtonProps
    ]);
    const InputButtonTrigger = useCallback(({ ref, ...props })=>/*#__PURE__*/ jsx(InputButton, {
            ...props,
            ref: mergeRefs(ref, inputButtonRef),
            onClick: onInputButtonClick,
            "data-testid": "base-autocomplete-input",
            iconLeft: iconName ?? void 0,
            label: label,
            value: inputValue,
            size: "lg",
            className: "w-full",
            errorMessage: shouldShowError ? errorMessage : void 0
        }), [
        iconName,
        label,
        inputValue,
        shouldShowError,
        errorMessage,
        onInputButtonClick,
        inputButtonRef
    ]);
    const itemCount = isGrouped ? autocompleteItems.reduce((acc, g)=>acc + g.items.length, 0) : autocompleteItems.length;
    const shouldShowPopup = itemCount > 0 || !!renderEmpty;
    const itemListContent = /*#__PURE__*/ jsx(ItemListContent, {
        filterChipData: filterChipData,
        handleFilterChipClick: handleFilterChipClick,
        selectedFilterChipData: selectedFilterChipData,
        onFilterChipMouseDown: focusAutocompleteInput,
        listSlot: listSlot,
        emptySlot: emptySlot,
        groupLabelSlot: groupLabelSlot,
        autocompleteItems: autocompleteItems,
        isGrouped: isGrouped,
        renderEmpty: renderEmpty,
        handleItemSelect: handleItemSelect,
        getItemFirstLineSuffix: getItemFirstLineSuffix
    });
    const getItemStringValue = useCallback((item)=>{
        if (!item) return '';
        if ('label' in item && 'items' in item) return '';
        return item.itemName ?? '';
    }, []);
    if (showDrawerOverlay) return /*#__PURE__*/ jsx("div", {
        className: cn(rootSlot(), className),
        children: /*#__PURE__*/ jsxs(Drawer.Root, {
            open: isOpen,
            snapPoints: [
                1
            ],
            onOpenChange: (open)=>{
                if (!open) handleCloseDrawer();
            },
            onOpenChangeComplete: (open)=>{
                if (open) handleDrawerOpenComplete();
            },
            children: [
                /*#__PURE__*/ jsx(Drawer.Trigger, {
                    render: /*#__PURE__*/ jsx(InputButtonTrigger, {})
                }),
                /*#__PURE__*/ jsxs(Drawer.Portal, {
                    children: [
                        /*#__PURE__*/ jsx(Drawer.Backdrop, {}),
                        /*#__PURE__*/ jsx(Drawer.Viewport, {
                            children: /*#__PURE__*/ jsxs(Drawer.Popup, {
                                ref: setOverlayPopupRef,
                                className: "p-0",
                                height: "full",
                                initialFocus: false,
                                children: [
                                    /*#__PURE__*/ jsx(Drawer.Title, {
                                        className: "sr-only",
                                        children: "Autocomplete"
                                    }),
                                    /*#__PURE__*/ jsxs(Autocomplete.Root, {
                                        open: isOpen,
                                        value: inputValue,
                                        onValueChange: handleInputValueChange,
                                        items: autocompleteItems,
                                        itemToStringValue: getItemStringValue,
                                        mode: "none",
                                        autoHighlight: autoHighlight,
                                        children: [
                                            /*#__PURE__*/ jsx(OverlaySearchHeader, {
                                                children: /*#__PURE__*/ jsxs("div", {
                                                    className: overlayInputSlot(),
                                                    children: [
                                                        /*#__PURE__*/ jsx("div", {
                                                            className: iconSlot(),
                                                            children: /*#__PURE__*/ jsx(IconButton, {
                                                                onClick: handleCloseDrawer,
                                                                iconName: "arrow_back",
                                                                type: "plainPrimary",
                                                                shadow: true,
                                                                size: "md",
                                                                "aria-label": "Close drawer"
                                                            })
                                                        }),
                                                        /*#__PURE__*/ jsx(Autocomplete.Input, {
                                                            render: inputTextTrigger
                                                        })
                                                    ]
                                                })
                                            }),
                                            /*#__PURE__*/ jsx("div", {
                                                className: "max-h-[calc(var(--vvh,100dvh)-6rem)] overflow-y-auto overscroll-contain px-2 py-0",
                                                children: itemListContent
                                            })
                                        ]
                                    })
                                ]
                            })
                        })
                    ]
                })
            ]
        })
    });
    return /*#__PURE__*/ jsx(Autocomplete.Root, {
        open: isOpen,
        value: inputValue,
        onOpenChange: onOpenChange,
        onValueChange: handleInputValueChange,
        items: autocompleteItems,
        itemToStringValue: getItemStringValue,
        mode: "none",
        autoHighlight: autoHighlight,
        children: /*#__PURE__*/ jsxs("div", {
            className: cn(rootSlot(), className),
            children: [
                /*#__PURE__*/ jsx(Autocomplete.Input, {
                    render: inputTextTrigger
                }),
                shouldShowPopup ? /*#__PURE__*/ jsx(Autocomplete.Portal, {
                    container: portalContainer || void 0,
                    children: /*#__PURE__*/ jsx(Autocomplete.Positioner, {
                        className: positionerSlot(),
                        sideOffset: 4,
                        align: "start",
                        collisionAvoidance: collisionAvoidance,
                        positionMethod: "fixed",
                        children: /*#__PURE__*/ jsx(Autocomplete.Popup, {
                            className: popupSlot(),
                            "data-base-autocomplete-popup": true,
                            [DRAWER_SWIPE_IGNORE_ATTRIBUTE]: '',
                            children: itemListContent
                        })
                    })
                }) : null
            ]
        })
    });
}
export { BaseAutocomplete };
